import React from "react";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/card";
import { 
  Users, 
  FileText, 
  PlayCircle,
  FileBarChart2 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ApiDebug from '../components/ApiDebug';

// Force component version update
const BUILD_VERSION = Date.now();

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  
  console.log(`AdminPanel rendering with build version: ${BUILD_VERSION}`);
  
  const handleNavigateToUsers = () => navigate('/admin/users');
  const handleNavigateToMotions = () => navigate('/admin/motions');
  const handleNavigateToRounds = () => navigate('/admin/rounds');
  const handleNavigateToLogs = () => navigate('/admin/logs');
  
  return (
    <div className="p-4 w-full max-w-4xl">
      <h1 className="text-2xl font-bold mb-6 dark:text-white light:text-gray-800">
        Admin Dashboard
      </h1>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
        {/* User Management Card */}
        <Card className="shadow-lg bg-card">
          <CardHeader>
            <CardTitle className="flex items-center text-foreground">
              <Users className="mr-2" size={22} />
              User Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
              Manage users, view profiles and adjust permissions in the system.
            </p>
            <Button 
              onClick={handleNavigateToUsers}
              variant="action"
              className="w-full"
            >
              <span>Manage Users</span>
            </Button>
          </CardContent>
        </Card>

        {/* Motion Management Card */}
        <Card className="shadow-lg bg-card">
          <CardHeader>
            <CardTitle className="flex items-center text-foreground">
              <FileText className="mr-2" size={22} />
              Motion Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
              Create, update or delete debate motions. Organize motions by theme and competition type.
            </p>
            <Button 
              onClick={handleNavigateToMotions}
              variant="action"
              className="w-full"
            >
              <span>Manage Motions</span>
            </Button>
          </CardContent>
        </Card>

        {/* Debate Round Card */}
        <Card className="shadow-lg bg-card">
          <CardHeader>
            <CardTitle className="flex items-center text-foreground">
              <PlayCircle className="mr-2" size={22} />
              Debate Rounds
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
              Create new debate rounds, manage allocations, and view round statistics.
            </p>
            <Button 
              onClick={handleNavigateToRounds}
              variant="action"
              className="w-full"
            >
              <span>Manage Rounds</span>
            </Button>
          </CardContent>
        </Card>

        {/* Activity Logs Card - Always include this */}
        <Card className="shadow-lg bg-card" data-testid="logs-card">
          <CardHeader>
            <CardTitle className="flex items-center text-foreground">
              <FileBarChart2 className="mr-2" size={22} />
              Activity Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
              Monitor user activity across rounds. View and export attendance logs and system events.
            </p>
            <Button 
              onClick={handleNavigateToLogs}
              variant="action"
              className="w-full"
              data-testid="logs-button"
            >
              <span>View Logs</span>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Optional: API Debug component for testing during development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-8">
          <ApiDebug />
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
